import { useState } from 'react';
import {
  Button,
  ConfirmModal,
  Form,
  FormItem,
  GridList,
  Input,
  Loading,
  Modal,
  SearchPanel,
} from '../../../../components';
import { KnowledgeCard } from './components/KnowledgeCard';
import { useKnowledgeManagement } from './hooks/useKnowledgeManagement';
import KnowledgeBaseForm from './components/KnowledgeBaseForm';
import { SearchIcon } from '../../../../components/SearchBar/SearchIcon';
import type { ActionCardPayload } from './components/KnowledgeCard';

export type ActionType =
  | 'create'
  | 'rename'
  | 'delete'
  | 'activate'
  | 'details'
  | 'edit_chunk'
  | 'download';
export const KnowledgeBaseList = () => {
  const {
    knowlegeList,
    loading,
    setFilters,
    filters,
    isOpened,
    onClose,
    onOpen,
    handleCreateBaseKnowledge,
    creatingBaseKnowledge,
    handleRenameBaseKnowledge,
    isOpenedDeleteModal,
    onCloseDeleteModal,
    onOpenDeleteModal,
    handleDeleteKnowledgeBase,
    handleDownloadKnowledgeBase,
    downloadingKnowledgeBase,
    pagination,
    setPage,
  } = useKnowledgeManagement();

  const [actionPayload, setActionPayload] = useState<ActionCardPayload | null>(
    null
  );

  const handleCardAction = async (payload: ActionCardPayload) => {
    if (payload.action === 'rename') {
      setActionPayload(payload);
      onOpen();
    }

    if (payload.action === 'delete') {
      onOpenDeleteModal();
      setActionPayload(payload);
    }

    if (payload.action === 'download') {
      await handleDownloadKnowledgeBase(payload.id || '', payload.name || '');
    }
  };

  const handleSaveData = (data: any) => {
    switch (actionPayload?.action) {
      case 'create':
        handleCreateBaseKnowledge(data);
        break;
      case 'rename':
        handleRenameBaseKnowledge(
          actionPayload?.id || '',
          data.name,
          data.metadata_fields
        );
        break;
      case 'delete':
        break;
      default:
        break;
    }
  };

  if (loading) return <Loading />;

  return (
    <>
      {downloadingKnowledgeBase && <Loading />}
      <div className="flex flex-wrap justify-between items-center mb-4">
        <div className="flex gap-x-sm justify-between">
          <Form initialValue={filters ?? {}} onChange={setFilters}>
            <FormItem inline={false} name="name" label="">
              <Input
                placeholder="Search"
                inputClassName="pl-20 text-medium"
                prefix={<SearchIcon />}
              />
            </FormItem>
          </Form>
        </div>

        <Button
          label="Create New"
          onClick={() => {
            onOpen();
            setActionPayload({ action: 'create' });
          }}
          size="medium"
        />
      </div>

      <Modal
        size="small"
        open={isOpened}
        onClose={onClose}
        title={`${actionPayload?.action === 'rename' ? 'Rename' : 'New'} Knowledge Base`}
      >
        <KnowledgeBaseForm
          key={'new'}
          onCancel={onClose}
          actionType={actionPayload?.action || 'create'}
          saving={creatingBaseKnowledge}
          onSave={handleSaveData}
          values={{
            name: actionPayload?.name || '',
            metadata_fields: actionPayload?.metadata_fields || '',
          }}
        />
      </Modal>
      <ConfirmModal
        title="Do you want to delete this knowledge base?"
        open={isOpenedDeleteModal}
        onClose={onCloseDeleteModal}
        onConfirm={() => handleDeleteKnowledgeBase(actionPayload?.id || '')}
      />
      <div className="mx-sm">
        {!knowlegeList.length ? (
          <div className="sm:max-w-screen-sm md:max-w-screen-md xl:max-w-screen-xl xl:mx-auto md:mx-auto sm:mx-auto mx-sm">
            <div className="flex flex-col justify-center items-center h-full text-3xl border border-border bg-gray-100 rounded-lg text-secondary p-10">
              No Data
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center pt-[8rem] pb-lg">
            <div className="w-full max-w-screen-xl">
              <GridList
                items={knowlegeList}
                gap="gap-14"
                className="grid w-full grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
                renderItem={(item, index) => (
                  <KnowledgeCard
                    key={index}
                    item={item}
                    onClick={handleCardAction}
                  />
                )}
              />
            </div>
          </div>
        )}
        {knowlegeList.length > 0 && (
          <div className="mt-sm flex justify-between items-center">
            <SearchPanel
              totalItems={pagination?.totalItems || 0}
              page={pagination?.page}
              perPage={20}
              withPaginate
              setPage={setPage}
            />
          </div>
        )}
      </div>
    </>
  );
};
